import { forwardRef, type InputHTMLAttributes, createContext, useContext } from 'react'
import { cn } from '@lib/utils'

export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** Radio label */
  label?: string
  /** Description below label */
  description?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Radio button component with brutalist styling
 *
 * @example
 * <Radio name="choice" value="a" label="Option A" />
 * <Radio name="choice" value="b" label="Option B" />
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      className,
      label,
      description,
      size = 'md',
      disabled,
      id,
      ...props
    },
    ref
  ) => {
    const radioId = id || `radio-${Math.random().toString(36).slice(2, 9)}`

    return (
      <div className={cn('flex items-start gap-3', className)}>
        <div className="relative flex items-center">
          <input
            ref={ref}
            type="radio"
            id={radioId}
            disabled={disabled}
            className="peer sr-only"
            {...props}
          />
          <div
            className={cn(
              // Base styles
              'flex items-center justify-center rounded-full border-2 border-border bg-surface',
              'transition-all cursor-pointer',
              'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',

              // Size
              size === 'sm' && 'h-4 w-4',
              size === 'md' && 'h-5 w-5',
              size === 'lg' && 'h-6 w-6',

              // Checked state
              'peer-checked:border-primary',

              // Disabled state
              'peer-disabled:cursor-not-allowed peer-disabled:opacity-50',

              // Hover (when not disabled)
              'peer-enabled:hover:border-primary'
            )}
            onClick={() => {
              if (!disabled) {
                document.getElementById(radioId)?.click()
              }
            }}
          >
            {/* Inner dot */}
            <div
              className={cn(
                'rounded-full bg-primary scale-0 transition-transform',
                'peer-checked:scale-100',
                size === 'sm' && 'h-2 w-2',
                size === 'md' && 'h-2.5 w-2.5',
                size === 'lg' && 'h-3 w-3'
              )}
            />
          </div>
        </div>

        {(label || description) && (
          <div className="flex flex-col">
            {label && (
              <label
                htmlFor={radioId}
                className={cn(
                  'text-text-primary cursor-pointer select-none',
                  size === 'sm' && 'text-body-sm',
                  size === 'md' && 'text-body',
                  size === 'lg' && 'text-body-lg',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-caption text-text-muted mt-0.5">{description}</p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Radio.displayName = 'Radio'

/**
 * Radio group context
 */
interface RadioGroupContextValue {
  name: string
  value?: string
  onChange?: (value: string) => void
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null)

function useRadioGroup() {
  return useContext(RadioGroupContext)
}

/**
 * Radio option type for groups
 */
export interface RadioOption {
  value: string
  label: string
  description?: string
}

/**
 * Radio group for single selection
 */
export interface RadioGroupProps {
  /** Group name (required for form submission) */
  name: string
  /** Group label */
  label?: string
  /** Radio options */
  options: RadioOption[]
  /** Selected value */
  value?: string
  /** Change handler */
  onChange?: (value: string) => void
  /** Orientation */
  orientation?: 'horizontal' | 'vertical'
  /** Size */
  size?: 'sm' | 'md' | 'lg'
  /** Disabled state */
  disabled?: boolean
  /** Error message */
  error?: string
}

export function RadioGroup({
  name,
  label,
  options,
  value,
  onChange,
  orientation = 'vertical',
  size = 'md',
  disabled,
  error,
}: RadioGroupProps) {
  return (
    <RadioGroupContext.Provider value={{ name, value, onChange, size, disabled }}>
      <fieldset className="space-y-3">
        {label && (
          <legend className="text-body-sm font-medium text-text-primary mb-2">
            {label}
          </legend>
        )}
        <div
          role="radiogroup"
          className={cn(
            'flex gap-4',
            orientation === 'vertical' && 'flex-col',
            orientation === 'horizontal' && 'flex-row flex-wrap'
          )}
        >
          {options.map((option) => (
            <Radio
              key={option.value}
              name={name}
              value={option.value}
              label={option.label}
              description={option.description}
              checked={value === option.value}
              onChange={() => onChange?.(option.value)}
              size={size}
              disabled={disabled}
            />
          ))}
        </div>
        {error && <p className="text-caption text-error mt-1">{error}</p>}
      </fieldset>
    </RadioGroupContext.Provider>
  )
}

export default Radio
